import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabasePublic } from '../supabaseClient';
import { getBackendURL } from '../env';

const EmailConfirmation = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Confirming your email...');

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Supabase puts the tokens in the URL hash automatically
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        console.log('📧 [CONFIRM] Email confirmation page loaded', {
          has_token: !!accessToken,
          has_refresh: !!refreshToken,
          type: type,
          full_hash: window.location.hash,
        });

        if (accessToken) {
          console.log('✅ [CONFIRM] Valid confirmation token found');

          // Log current time to check for clock skew
          const now = Math.floor(Date.now() / 1000);
          console.log('🕐 [CONFIRM] Current device time (unix):', now);
          console.log('🕐 [CONFIRM] Current device time (readable):', new Date().toISOString());

          // Set flag to tell other components not to access auth
          localStorage.setItem('auth_confirmation_in_progress', 'true');
          console.log('🚩 [CONFIRM] Set confirmation flag in localStorage');

          // Wait longer to avoid race conditions with other auth listeners
          // Other components (activity tracker, credit tracker) also initialize on mount
          await new Promise(resolve => setTimeout(resolve, 2000));

          console.log('🔐 [CONFIRM] Setting session after delay...');

          // Set the session using the access token
          let data, error;
          try {
            const result = await supabasePublic().auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            data = result.data;
            error = result.error;

            console.log('📊 [CONFIRM] setSession result:', {
              has_data: !!data,
              has_error: !!error,
              has_user: !!data?.user,
              has_session: !!data?.session,
              error_message: error?.message,
              error_name: error?.name,
              error_status: error?.status,
            });
          } catch (setSessionError) {
            console.error('💥 [CONFIRM] Exception during setSession:', {
              error: setSessionError,
              error_message: setSessionError?.message,
              error_name: setSessionError?.name,
              error_stack: setSessionError?.stack,
            });
            setStatus('error');
            setMessage('Error confirming your email. Please try again or contact support.');
            return;
          }

          if (error) {
            console.error('❌ [CONFIRM] Error setting session:', {
              error,
              message: error.message,
              name: error.name,
              status: error.status,
            });
            setStatus('error');
            setMessage('Error confirming your email. Please try again or contact support.');
            return;
          }

          console.log('✅ [CONFIRM] Session set successfully', {
            user_email: data.user?.email,
            user_id: data.user?.id,
            has_session: !!data.session,
            session_expires_at: data.session?.expires_at,
          });

          // User is now signed in! Update their role from unauthenticated to user
          const userId = data.user?.id;
          const accountType = data.user?.user_metadata?.account_type || 'doctor';

          console.log('🔍 [CONFIRM] Updating user role from unauthenticated to user...', {
            user_id: userId,
            account_type: accountType,
            user_metadata: data.user?.user_metadata,
          });

          // Only update the role if it is still in the unauthenticated,{accountType} state.
          // Approved users (e.g. 'socialworker') or pending users ('user,socialworker') must
          // not be overwritten here.
          let currentRole = null;
          try {
            const { data: userRow } = await supabasePublic()
              .from('users')
              .select('role')
              .eq('id', userId)
              .maybeSingle();
            currentRole = userRow?.role || null;
          } catch (e) {
            console.warn('⚠️ [CONFIRM] Could not fetch current role, skipping role update:', e);
          }

          console.log('🔍 [CONFIRM] Current role in DB:', currentRole);

          if (currentRole && currentRole.startsWith('unauthenticated,')) {
            const newRoleValue = `user,${accountType}`;
            console.log('📝 [CONFIRM] Updating role from', currentRole, '→', newRoleValue);
            let updateError;
            try {
              const result = await supabasePublic()
                .from('users')
                .update({ role: newRoleValue })
                .eq('id', userId);
              updateError = result.error;
            } catch (e) {
              console.error('💥 [CONFIRM] Exception updating user role:', e);
              updateError = e;
            }
            if (updateError) {
              console.error('❌ [CONFIRM] Error updating user role:', updateError?.message);
            } else {
              console.log('✅ [CONFIRM] User role updated to', newRoleValue);
            }
          } else {
            console.log('ℹ️ [CONFIRM] Role is already set (', currentRole, '), skipping update');
          }

          // For students, social workers, and other (MISC), automatically create their schema.
          // Only do this on first confirmation (when role was unauthenticated,X) to avoid
          // overwriting an approved role on subsequent sign-ins.
          if (currentRole && currentRole.startsWith('unauthenticated,') &&
              (accountType === 'student' || accountType === 'socialworker' || accountType === 'other')) {
            console.log('📦 [CONFIRM] Creating schema for', accountType);

            // Same-origin in prod, localhost:8001 in dev. Hardcoding
            // notesai-c.onrender.com here made this a cross-origin call from
            // app.magicnotesai.com and tripped CORS (same bug as signup).
            const backendUrl = getBackendURL();

            try {
              const schemaResponse = await fetch(`${backendUrl}/api/assign-user-schema/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_id: userId,
                  account_type: accountType,
                }),
              });

              const schemaResult = await schemaResponse.json();

              if (schemaResponse.ok) {
                console.log('✅ [CONFIRM] Schema created successfully:', schemaResult);
              } else {
                console.error('❌ [CONFIRM] Error creating schema:', schemaResult);
              }
            } catch (schemaError) {
              console.error('💥 [CONFIRM] Exception creating schema:', schemaError);
              // Don't block the user flow - they can still use the app
            }
          }

          setStatus('success');
          setMessage('Email confirmed successfully! Redirecting to your dashboard...');

          // Clear the confirmation flag
          localStorage.removeItem('auth_confirmation_in_progress');
          console.log('✅ [CONFIRM] Cleared confirmation flag from localStorage');

          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname);

          // Redirect to schedules after 2 seconds (user is already signed in)
          setTimeout(() => {
            navigate('/schedules');
          }, 2000);
        } else {
          console.log('⚠️ [CONFIRM] No confirmation token found in URL');
          setStatus('info');
          setMessage('No confirmation token found. Redirecting to sign in...');
          setTimeout(() => {
            navigate('/auth');
          }, 2000);
        }
      } catch (error) {
        console.error('💥 [CONFIRM] Exception during confirmation:', error);
        localStorage.removeItem('auth_confirmation_in_progress');
        setStatus('error');
        setMessage('An error occurred. Please try signing in.');
        setTimeout(() => {
          navigate('/auth');
        }, 3000);
      }
    };

    handleEmailConfirmation();
  }, [navigate]);

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#4CAF50';
      case 'error':
        return '#f44336';
      case 'processing':
        return '#2196F3';
      default:
        return '#FF9800';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'processing':
        return '⏳';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      textAlign: 'center',
      backgroundColor: '#f5f5f5',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '500px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '20px',
        }}>
          {getStatusIcon()}
        </div>

        <h2 style={{
          color: getStatusColor(),
          marginBottom: '16px',
          fontSize: '24px',
        }}>
          {status === 'success' && 'Email Confirmed!'}
          {status === 'error' && 'Confirmation Failed'}
          {status === 'processing' && 'Confirming Email...'}
          {status === 'info' && 'Redirecting...'}
        </h2>

        <p style={{
          color: '#666',
          fontSize: '16px',
          lineHeight: '1.5',
        }}>
          {message}
        </p>

        {status === 'processing' && (
          <div style={{
            marginTop: '24px',
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2196F3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default EmailConfirmation;
